import axios, { AxiosError } from 'axios';
import MainURL from '../../../MainURL';
import { attachTokenRefresh } from '../../../authInterceptor';

// 지원 상태 관리 API (server/routers/resume/ApplicationStatusRouter.js)
// 요청자는 서버가 로그인 토큰으로 판정한다 — userAccount 를 보내지 않는다.

export type ApplicationStatusCode =
  | 'SUBMITTED'
  | 'VIEWED'
  | 'ON_HOLD'
  | 'CONTACTED'
  | 'INTERVIEW'
  | 'OFFERED'
  | 'ACCEPTED'
  | 'REJECTED'
  | 'WITHDRAWN';
export type ApplicationGroup = 'all' | 'reviewing' | 'active' | 'closed';
export type ApplicationCounts = Record<ApplicationGroup, number>;

export const STATUS_LABEL: Record<ApplicationStatusCode, string> = {
  SUBMITTED: '지원함',
  VIEWED: '열람됨',
  ON_HOLD: '보류',
  CONTACTED: '연락받음',
  INTERVIEW: '면접',
  OFFERED: '청빙 제안',
  ACCEPTED: '수락',
  REJECTED: '불합격',
  WITHDRAWN: '지원취소',
};

export const GROUP_TABS: { key: ApplicationGroup; label: string }[] = [
  { key: 'all', label: '전체' },
  { key: 'reviewing', label: '검토중' },
  { key: 'active', label: '진행중' },
  { key: 'closed', label: '종료' },
];

// 교회 화면 버튼 라벨 (전이 대상 상태 → 버튼 문구)
export const CHURCH_ACTION_LABEL: Record<string, string> = {
  VIEWED: '검토 재개',
  ON_HOLD: '보류',
  CONTACTED: '연락',
  INTERVIEW: '면접',
  OFFERED: '청빙 제안',
  REJECTED: '불합격',
};

export interface JobSummary {
  id: number | null;
  type: 'minister' | 'overseas';
  title: string;
  church: string;
  region: string;
  path?: string;
  deleted: boolean;
  closed?: boolean;
  deadlineAt?: string | null;
  daysToDeadline?: number | null;
  payMissing?: boolean;
  deadlineMissing?: boolean;
}

export interface TimelineItem {
  status: ApplicationStatusCode;
  label: string;
  at: string;
  actor: 'SEEKER' | 'CHURCH' | 'SYSTEM';
  memo: string | null;
  interviewAt: string | null;
}

export interface MessageItem {
  id: number;
  from: 'SEEKER' | 'CHURCH';
  mine: boolean;
  body: string;
  at: string;
}

export interface SimilarRecruit {
  path: string;
  title: string;
  church: string;
  region: string;
}

export interface SeekerListItem {
  id: number;
  status: ApplicationStatusCode;
  statusLabel: string;
  group: ApplicationGroup;
  appliedAt: string;
  statusChangedAt: string;
  note: string | null;
  deliveryState?: string | null;
  actions: string[];
  job: JobSummary;
}

export interface SeekerDetail {
  id: number;
  status: ApplicationStatusCode;
  statusLabel: string;
  version: number;
  appliedAt: string;
  statusChangedAt: string;
  interviewAt: string | null;
  wasViewed: boolean;
  /** 가져온 공고의 예약 지원 — HELD(교회 확인 대기) · DELIVERED · EXPIRED_NOTICE(원문 직접 지원 안내) · WITHDRAWN */
  deliveryState?: string | null;
  deliveryNote?: string | null;
  originalUrl?: string | null;
  churchMemo: string | null;
  job: JobSummary;
  timeline: TimelineItem[];
  messages: MessageItem[];
  actions: string[];
  similar: SimilarRecruit[];
}

export interface ApplicantSummary {
  name: string;
  gender: string;
  age: number | null;
  region: string;
  careerYears: number;
  education: string;
  prefs: { type: string; roles: string; depts: string; location: string };
  contact: { phone: string; email: string } | null;
}

export interface ChurchListItem {
  id: number;
  status: ApplicationStatusCode;
  statusLabel: string;
  group: ApplicationGroup;
  version: number;
  appliedAt: string;
  statusChangedAt: string;
  applicant: ApplicantSummary;
  actions: string[];
}

export interface ChurchDetail {
  id: number;
  status: ApplicationStatusCode;
  statusLabel: string;
  version: number;
  appliedAt: string;
  statusChangedAt: string;
  interviewAt: string | null;
  contactRevealedAt: string | null;
  churchMemo: string | null;
  applicant: ApplicantSummary;
  resumeSnapshot: any;
  job: JobSummary;
  timeline: TimelineItem[];
  messages: MessageItem[];
  actions: string[];
}

const api = axios.create({ baseURL: `${MainURL}/applications` });
api.interceptors.request.use((config) => {
  // 로그인 시 저장되는 토큰 (useSermonSupport 등과 동일)
  const token = localStorage.getItem('refreshToken');
  if (token) config.headers.set('Authorization', `Bearer ${token}`);
  return config;
});
attachTokenRefresh(api);

/** 409(충돌)·422(불가 전이)·403(권한)을 구분해 안내 문구를 돌려준다 (9.1) */
export function errorMessage(error: unknown, fallback = '처리하지 못했습니다.') {
  const err = error as AxiosError<{ error?: string }>;
  const status = err.response?.status;
  const message = err.response?.data?.error;
  if (status === 401) return message || '로그인이 필요합니다.';
  if (status === 409) return message || '다른 곳에서 상태가 변경되었습니다. 새로고침 해주세요.';
  return message || fallback;
}
export const isConflict = (error: unknown) => (error as AxiosError).response?.status === 409;

export const seekerApi = {
  list: (group: ApplicationGroup) =>
    api.get<{ items: SeekerListItem[]; counts: ApplicationCounts }>('/seeker/list', { params: { group } }).then((r) => r.data),
  detail: (id: string | number) => api.get<{ application: SeekerDetail }>(`/seeker/${id}`).then((r) => r.data.application),
  withdraw: (id: number, version: number, reason?: string) => api.post(`/seeker/${id}/withdraw`, { version, reason }),
  accept: (id: number, version: number, message?: string) => api.post(`/seeker/${id}/accept`, { version, message }),
  decline: (id: number, version: number) => api.post(`/seeker/${id}/decline`, { version }),
};

export const churchApi = {
  board: (recruitType: string, recruitId: string | number, group: ApplicationGroup) =>
    api
      .get<{ job: JobSummary; items: ChurchListItem[]; counts: ApplicationCounts; showBulkView: boolean }>(
        `/church/${recruitType}/${recruitId}`,
        { params: { group } },
      )
      .then((r) => r.data),
  detail: (id: string | number) => api.get<{ application: ChurchDetail }>(`/church/application/${id}`).then((r) => r.data.application),
  changeStatus: (id: number, body: { to: string; version: number; memo?: string; interviewAt?: string }) =>
    api.patch(`/church/application/${id}/status`, body),
  undo: (id: number) => api.post(`/church/application/${id}/undo`),
  bulkView: (ids: number[]) => api.post('/church/bulk-view', { ids }),
};

export const sendMessage = (id: number, body: string) =>
  api.post<{ message: MessageItem }>(`/${id}/messages`, { body }).then((r) => r.data.message);

/** 최근 6시간 내 상태가 바뀐 항목 강조 (4.2 ②) */
export const changedRecently = (at: string) => {
  const t = new Date(at.replace(' ', 'T')).getTime();
  return Number.isFinite(t) && Date.now() - t < 6 * 60 * 60 * 1000;
};

/** '2026-10-07 14:22' → '오늘' / '어제' / 'N일 전' / '2026.10.01' */
export function relativeDay(at: string) {
  const d = new Date(at.replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return at;
  const today = new Date();
  const diff = Math.floor((new Date(today.toDateString()).getTime() - new Date(d.toDateString()).getTime()) / 86400000);
  if (diff <= 0) return '오늘';
  if (diff === 1) return '어제';
  if (diff < 7) return `${diff}일 전`;
  return at.slice(0, 10).replace(/-/g, '.');
}

// ---------------------------------------------------------------------------------
// 스카우트 (인재 검색 · 제안) — application-status-design.md 7장
// ---------------------------------------------------------------------------------
export interface TalentItem {
  resumeId: number;
  score: number;
  reasons: string[];
  name: string;
  gender: string;
  age: number | null;
  region: string;
  careerYears: number;
  education: string;
  prefs: { type: string; roles: string; depts: string; location: string };
  offerStatus: 'PENDING' | 'ACCEPTED' | 'DECLINED' | null;
}

export interface ScoutOffer {
  id: number;
  status: 'PENDING' | 'ACCEPTED' | 'DECLINED';
  message: string | null;
  createdAt: string;
  applicationId: number | null;
  job: JobSummary;
}

export const scoutApi = {
  optIns: () => api.get<{ items: { resumeId: number; scoutOptIn: boolean }[] }>('/seeker/scout-optins').then((r) => r.data.items),
  setOptIn: (resumeId: number | string, optIn: boolean) => api.post('/seeker/scout-optins', { resumeId, optIn }),
  offers: () => api.get<{ items: ScoutOffer[] }>('/seeker/offers').then((r) => r.data.items),
  respond: (offerId: number, accept: boolean) =>
    api.post<{ status: string; applicationId: number | null }>(`/seeker/offers/${offerId}/${accept ? 'accept' : 'decline'}`).then((r) => r.data),
  talent: (recruitType: string, recruitId: string | number) =>
    api.get<{ job: JobSummary; items: TalentItem[] }>(`/church/${recruitType}/${recruitId}/talent`).then((r) => r.data),
  send: (recruitType: string, recruitId: string | number, resumeId: number, message?: string) =>
    api.post(`/church/${recruitType}/${recruitId}/scout`, { resumeId, message }),
};
